import {
  goToKeys,
  pinCommand,
  placeKey,
  railCommand,
  ShortcutKeys,
  screenActions,
  shortcutKeys,
  themeCommand,
  usePins,
  useRail,
  useRecentVisits,
} from "@nexxt/app-kit";
import { EXACT_CODE_SCORE } from "@nexxt/contracts";
import { CommandPalette, Highlight, type PaletteGroup, type PaletteItem, useMediaQuery, wideLayout } from "@nexxt/ui";
import { useNavigate } from "@tanstack/react-router";
import {
  Bell,
  Keyboard,
  ListFilter,
  Moon,
  PanelLeftClose,
  PanelLeftOpen,
  Pin,
  PinOff,
  Search,
  TriangleAlert,
} from "lucide-react";
import { useState } from "react";
import { m } from "../../paraglide/messages.js";
import { highlightRanges } from "../features/search/highlight";
import { hitTarget } from "../features/search/hit-targets";
import type { SearchHit } from "../features/search/search.queries";
import { searchDomainLabels, searchTypes, subtitleOf } from "../features/search/search-types";
import { useNamedQueues, useSearchResults } from "../features/search/use-search";
import { type NavItem, sectionLabel } from "./navigation";

// ⌘K (design-system.md §4.10): with nothing typed, the pages opened most and lately; the actions of the screen on
// display, and pinning its place; the places with their section and key; the preferences (ADR-114). With a text, also
// what the domains find, a group per type, the code typed exactly first, and the lists of the states the text names;
// the search page has the rest (ADR-117).

const RECENT_SHOWN = 6;
const HITS_SHOWN = 5;

// The ranks of what is not matched again on the client (PaletteItem.rank): a code typed exactly, then the lists a
// state names, then the hits in the order of their domain, those of a type a code points to first. The way to the
// search page stays last.
const BEST_RANK = 1;
const LIST_RANK = 0.97;
const OTHER_TYPE = 0.9;
const LAST_RANK = 0;

interface CommandMenuProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  places: NavItem[];
  /** The place on display, which ⌘K can pin or unpin. */
  current: NavItem | undefined;
  onShortcuts: () => void;
}

export function CommandMenu({ open, onOpenChange, places, current, onShortcuts }: CommandMenuProps) {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const results = useSearchResults(search, { pageSize: HITS_SHOWN, enabled: open });
  const lists = useNamedQueues(search);
  const q = results.parsed.q;
  const visits = useRecentVisits();
  const pins = usePins();
  // the column of icons exists only where the sidebar is a column
  const wide = useMediaQuery(wideLayout);
  const narrow = useRail();
  const rail = railCommand(narrow);
  const theme = themeCommand();

  // stored by frecency; a visit shows only while its place is one the user can open
  const recent: PaletteItem[] = visits.flatMap((visit) => {
    const place = places.find((p) => p.id === visit.place);
    if (!place) return [];
    return [
      {
        id: `recent-${visit.key}`,
        label: visit.label ?? place.label(),
        ...(visit.label && { hint: place.label() }),
        icon: <place.icon />,
        run: () => void navigate({ href: visit.href }),
      },
    ];
  });

  // a place of a section, not Now: the sidebar has Now on top already
  const pinAction: PaletteItem[] = [];
  if (current?.section) {
    const pinned = pins.includes(placeKey(current.id));
    const pin = pinCommand(placeKey(current.id), current.label(), pinned);
    pinAction.push({ id: "pin", label: pin.label, icon: pinned ? <PinOff /> : <Pin />, run: pin.run });
  }

  const hitItem = (hit: SearchHit, rank: number, id = `${hit.type}-${hit.id}`): PaletteItem => {
    const { icon: Icon } = searchTypes[hit.type];
    const subtitle = subtitleOf(hit);
    return {
      id,
      label: hit.title,
      content: <Highlight text={hit.title} ranges={highlightRanges(hit.title, q)} />,
      ...(subtitle && { hint: subtitle }),
      icon: <Icon />,
      rank,
      run: () => void navigate(hitTarget(hit)),
    };
  };
  // only a code typed whole, never an equal name
  const best = results.groups.flatMap((g) => g.items).filter((hit) => hit.score === EXACT_CODE_SCORE);
  const typeGroups: PaletteGroup[] = results.groups.map((group) => ({
    heading: searchTypes[group.type].label(),
    items: group.items
      .filter((hit) => !best.includes(hit))
      .map((hit) => hitItem(hit, hit.score * (results.parsed.first.includes(group.type) ? 1 : OTHER_TYPE))),
  }));
  const toSearchPage = () => void navigate({ to: "/search", search: { q: search.trim() } });
  const searchItems: PaletteItem[] = !results.asked
    ? []
    : [
        ...results.failed.map((domain) => ({
          id: `search-failed-${domain}`,
          label: m.palette_search_failed({ domain: searchDomainLabels[domain]() }),
          icon: <TriangleAlert />,
          rank: LAST_RANK,
          run: toSearchPage,
        })),
        {
          id: "search-all",
          label: m.palette_see_all({ q: search.trim() }),
          ...(results.pending && { hint: m.palette_searching() }),
          icon: <Search />,
          rank: LAST_RANK,
          run: toSearchPage,
        },
      ];

  const groups: PaletteGroup[] = [
    { heading: m.palette_recent(), items: search === "" ? recent.slice(0, RECENT_SHOWN) : [] },
    {
      heading: m.palette_best_match(),
      items: best.map((hit) => hitItem(hit, BEST_RANK, `best-${hit.type}-${hit.id}`)),
    },
    {
      heading: m.palette_lists(),
      items: lists.map((queue) => ({
        id: `list-${queue.id}`,
        label: queue.title,
        ...(queue.data && { hint: String(queue.data.count) }),
        icon: <ListFilter />,
        rank: LIST_RANK,
        run: () => void navigate(queue.link),
      })),
    },
    { heading: m.palette_actions(), items: open ? [...screenActions(), ...pinAction] : [] },
    {
      heading: m.palette_go_to(),
      items: places.map((place) => {
        const section = sectionLabel(place);
        return {
          id: `go-${place.id}`,
          label: place.label(),
          ...(section && { hint: section, keywords: [section] }),
          icon: <place.icon />,
          ...(place.key && { shortcut: <ShortcutKeys keys={goToKeys(place.key)} /> }),
          run: () => void navigate({ to: place.to }),
        };
      }),
    },
    ...typeGroups,
    { heading: m.palette_search(), items: searchItems },
    {
      heading: m.palette_preferences(),
      items: [
        { id: "theme", label: theme.label, icon: <Moon />, run: theme.run },
        ...(wide
          ? [
              {
                id: "rail",
                label: rail.label,
                icon: narrow ? <PanelLeftOpen /> : <PanelLeftClose />,
                shortcut: <ShortcutKeys keys={shortcutKeys.rail} />,
                run: rail.run,
              },
            ]
          : []),
        {
          id: "shortcuts",
          label: m.shortcuts_title(),
          icon: <Keyboard />,
          shortcut: <ShortcutKeys keys={shortcutKeys.help} />,
          run: onShortcuts,
        },
        // the emails and the pushes the user turns off for themselves (ADR-149, ADR-283)
        {
          id: "notification-preferences",
          label: m.notification_preferences_title(),
          icon: <Bell />,
          run: () => void navigate({ to: "/settings/notifications" }),
        },
      ],
    },
  ];

  return (
    <CommandPalette
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next);
        if (!next) setSearch("");
      }}
      groups={groups}
      label={m.palette_label()}
      placeholder={m.palette_placeholder()}
      emptyText={m.palette_empty()}
      search={search}
      onSearchChange={setSearch}
    />
  );
}
